import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import dayjs from "dayjs";
import { GripVertical, Info, Pencil, Plus, RotateCcw, VolumeX, X } from "lucide-react";
import {
  Alert,
  Badge,
  BulkActionBar,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  FormModal,
  FormStack,
  Input,
  LoadingState,
  Modal,
  Page,
  Select,
  Switch,
  Text,
} from "@marekl/adminer/components";
import { notifyError, notifySuccess } from "@marekl/adminer/api";
import {
  formatDate,
  formatDateTime,
  formatDuration,
  formatTime,
  formatWeekday,
  pluralizeCzech,
} from "@marekl/adminer/format";
import { useConfirm, useRowSelection } from "@marekl/adminer/hooks";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  competitionCrumbs,
  lockedNotice,
  useCompetitionReadOnly,
} from "../../../shell/CompetitionLayout";
import { useCompetition } from "../../../lib/queries/competitions";
import { useCompetitionEntries } from "../../../lib/queries/entries";
import { useRuleSetDocument } from "../../../lib/queries/rules";
import { indexDocument } from "../../../lib/rulesDocument";
import { EntryDetailModal } from "../../../components/EntryDetailModal";
import { TeamFlag } from "../../../components/TeamFlag";
import {
  useAddSection,
  useBuildStartList,
  useDeleteBlock,
  useDeleteSection,
  useInsertBlock,
  usePublishStartList,
  usePublishStartListDraft,
  useRenameSection,
  useReorderEntries,
  useReorderItems,
  useReorderLane,
  useCreateLaneGroup,
  useUpdateLaneGroup,
  useScratchEntry,
  useStartList,
  useUnpublishStartList,
  useUpdateBlock,
  useUpdateDay,
  type BlockPayload,
  type StartListDay,
  type StartListDocument,
  type StartListItem,
  type StartListLane,
  type StartListLaneMode,
  type StartListSection,
} from "../../../lib/queries/startlists";
import {
  BLOCK_KIND_LABELS,
  EDIT_PUBLISHED_LABEL,
  LANE_MODE_LABELS,
  PUBLISH_DRAFT_LABEL,
  STAGE_LABELS,
  STAGE_VARIANT,
  formatDayTime,
} from "../../../lib/startListState";
import { isFrozen, liveAccess } from "../../../lib/competitionState";
import { MergeCategoriesModal } from "../MergeCategoriesModal";
import { buildSummary } from "./buildSummary";
import {
  ACTION_LABEL,
  FIRST_BUILD_LABEL,
  UNPUBLISH_LABEL,
  staleAction,
  staleCopy,
  staleTitle,
} from "./staleCopy";
import { BlockModal } from "./BlockModal";
import styles from "./StartListPage.module.css";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "block"; sectionId: ID; afterItemId?: ID; item?: StartListItem };

/** Full detail, or just the running order of the categories. */
type View = "entries" | "compact";

/**
 * The organizer's start list.
 *
 * The page mirrors the document's real shape — day, part, discipline
 * block, entries — so the no-split rule needs no enforcement here: an
 * entry can only be dragged among its own block's rows, and a whole
 * discipline moves as one object. There is no interaction that could
 * scatter a category, which is why the server can refuse the attempt
 * outright rather than the UI having to prevent it.
 *
 * Nothing recomputes a schedule. Times, numbers and headings arrive
 * resolved from the server, so the screen, the print-out and the judging
 * tablet cannot drift apart. The ONE thing the page holds itself is the
 * order during a drag — see `draft` below.
 */
export function StartListPage() {
  const confirm = useConfirm();
  const { id } = useParams();
  const compId = id ?? "";

  const { data: competition } = useCompetition(compId);
  const canUnpublish =
    competition?.status === "draft" ||
    competition?.status === "announced" ||
    competition?.status === "published";
  // Dokončeno or archived: the list is frozen with the competition. A
  // competitions_viewer reads it the same way: everything shown, no write.
  const readOnly = useCompetitionReadOnly();
  const frozen = isFrozen(competition) || readOnly;
  // In Probíhá every edit is a live command (R25), and the structure
  // (parts, days, Aktualizovat, publishing) is settled; while the hall
  // runs the competition this page only reads.
  const access = liveAccess(competition);
  const running = access === "live";
  const live = running ? compId : undefined;
  const { data: doc, isLoading, error, refetch } = useStartList(compId);
  const { data: ruleDoc } = useRuleSetDocument(competition?.rule_set_id);

  const build = useBuildStartList(compId);
  const publish = usePublishStartList(compId);
  const publishDraft = usePublishStartListDraft(compId);
  const unpublish = useUnpublishStartList(compId);
  const reorderEntries = useReorderEntries(live);
  const reorderItems = useReorderItems(live);
  const reorderLane = useReorderLane(live);
  const createLaneGroup = useCreateLaneGroup(live);
  const updateLaneGroup = useUpdateLaneGroup(live);
  const insertBlock = useInsertBlock(live);
  const updateBlock = useUpdateBlock(live);
  const deleteBlock = useDeleteBlock(live);
  const scratch = useScratchEntry(live);
  const addSection = useAddSection(live);
  const renameSection = useRenameSection(live);
  const deleteSection = useDeleteSection(live);
  const updateDay = useUpdateDay(live);

  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [view, setView] = useState<View>("entries");
  // Selection state comes from the library, uncontrolled: this page owns
  // no other copy of it.
  //
  // `rows` is nearly decorative here. The hook uses it only for the
  // whole-table answers this page never asks — selectedRows, allSelected,
  // someSelected, toggleAll — and the day/part buttons scope themselves by
  // handing toggleMany their own items, from which it derives the flip.
  // Passing an empty array would behave identically; every item in the
  // document is simply the honest thing to pass. For the same reason
  // withToggleAll is left alone: it shapes only the `selection` bag, which
  // this page does not use.
  //
  // From `doc`, not from `days`: `days` is the drag-time draft and is only
  // in scope after the loading guard below, while a hook may not be.
  const allBlocks = useMemo(
    () => (doc?.days ?? []).flatMap((d) => d.sections.flatMap(itemsDeep)),
    [doc],
  );
  const selection = useRowSelection<StartListItem>(allBlocks, (i) => i.id);
  const selected = useMemo(
    () => [...selection.selectedKeys] as ID[],
    [selection.selectedKeys],
  );
  const [mergeOpen, setMergeOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  // „Upravit zveřejněnou listinu": which publication the organizer unlocked,
  // so a later republish locks the screen again. Never stored (R16).
  const [unlockedFor, setUnlockedFor] = useState<number | null>(null);
  // Where a block dragged into or out of a lane will land (see onDragOver).
  const [dropHint, setDropHint] = useState<DropHint | null>(null);
  // Defaults to closing them: an organizer who has decided the running
  // order has, in practice, decided the field. The tick is there for the
  // provisional publish, which is the rarer case.
  // UNCHECKED by default.
  //
  // Publishing and closing přihlášky are two acts, and only the first is
  // the one being asked for. A pre-ticked box makes the second happen
  // unless the organizer notices it — and it shuts the door on every club
  // that has not entered yet, which no amount of undo brings back
  // (reopening means editing the uzávěrka on another screen).
  const [closeEntries, setCloseEntries] = useState(false);
  /**
   * The competition entry whose detail is open, or null.
   *
   * The list is fetched only once one is asked for. A running order can
   * carry a hundred and fifty starts, and the organizer arranging it
   * needs none of the rosters until they open one — so the query stays
   * disabled until then, and the dialog shows a loader for the moment it
   * takes. It shares its cache key with the Přihlášky and Hudba tabs,
   * so arriving from either costs nothing.
   */
  const [detailEntryId, setDetailEntryId] = useState<ID | null>(null);
  const { data: entries = [] } = useCompetitionEntries(
    detailEntryId === null ? undefined : compId,
  );
  const ruleIndex = useMemo(() => (ruleDoc ? indexDocument(ruleDoc) : null), [ruleDoc]);
  const detailEntry =
    detailEntryId === null ? null : (entries.find((e) => e.id === detailEntryId) ?? null);

  /**
   * The order as the organizer has just left it, held only until the
   * server confirms.
   *
   * Without it a drop had nowhere to land: the DOM still showed the old
   * order at the moment dnd-kit released the transform, so the block
   * snapped back to where it came from and only reappeared in its new
   * place a request later. It also gives cross-part dragging something
   * to do — the gap the block is about to occupy opens because the block
   * is genuinely there already.
   */
  const [draft, setDraft] = useState<StartListDay[] | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  // A refetch that is not ours (another tab, a rebuild) has to win, or
  // the page would keep showing a stale drag result forever.
  useEffect(() => {
    setDraft(null);
  }, [doc]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Without this the list is unreorderable by keyboard, which on a
    // document this long is the difference between usable and not.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const crumbs = competitionCrumbs(compId, competition, { label: "Startovní listina" });

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }

  // A 404 means the list has not been built yet — the normal starting
  // state, not a failure, so it gets an invitation rather than an error.
  const notBuilt = !doc;
  const onBuild = () =>
    build.mutate(undefined, {
      onSuccess: (res) => notifySuccess(buildSummary(res)),
      onError: (err) => notifyError(err),
    });

  if (notBuilt) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Header title="Startovní listina" subtitle={competition?.name} />
          <Page.Content stack gap="lg">
            {error && !isNotFound(error) ? (
              <ErrorState
                title="Startovní listinu se nepodařilo načíst"
                error={error}
                onRetry={refetch}
              />
            ) : (
              <EmptyState
                title="Listina zatím není sestavená"
                description="Sestaví se z podaných přihlášek: každá soutěžní kategorie dostane svůj blok a přihlášky se do něj seřadí podle čísla přihlášky."
                action={
                  <Button variant="primary" onClick={onBuild} loading={build.isPending}>
                    Sestavit listinu
                  </Button>
                }
              />
            )}
          </Page.Content>
        </Page>
      </>
    );
  }

  const days = draft ?? doc.days;
  const stale = doc.stale_reasons ?? [];
  // A list with days and parts but nothing in them: the state every
  // competition starts in now that the list is created with it. Worth
  // distinguishing, because the button that fills it is doing a first
  // build rather than an update, and the page should say so.
  const isEmpty = doc.days.every((day) =>
    day.sections.every((section) => section.items.length === 0),
  );
  // The one drift that blocks publishing — see ErrOrphanedDay.
  const hasOrphanDay = doc.days.some(
    (day) => day.outside_competition && day.sections.some((s) => s.items.length > 0),
  );
  // The deadline alone, not `entry_phase`: a competition closed because
  // it is FULL is a different situation, and offering to move its
  // uzávěrka would change a fact nobody asked to change. Mirrors
  // startlist.EntriesOpenAt.
  const entriesStillOpen =
    !doc.published &&
    competition?.status === "published" &&
    competition.entry_deadline != null &&
    dayjs().isBefore(competition.entry_deadline);
  // Publishing hands who competes to the organizer alone (R16), so a
  // club's remaining uzávěrka změn becomes a dead letter the moment the
  // listina goes out. That is a legitimate thing to do (a provisional
  // order is better served by the návrh, though) but not a silent one: an organizer who
  // set a late uzávěrka změn soutěžících meant clubs to be able to use
  // it right up to it.
  //
  // Gated on a STORED taper, not the effective one: with neither column
  // set the effective roster deadline IS the uzávěrka přihlášek, and
  // the warning would fire beside the „Zároveň uzavřít přihlášky" note
  // about a date the organizer only ever set as that.
  const changesStillOpen =
    !doc.published &&
    competition?.status === "published" &&
    (competition.entry_category_deadline != null || competition.entry_roster_deadline != null) &&
    competition.effective_entry_roster_deadline != null &&
    dayjs().isBefore(competition.effective_entry_roster_deadline);
  // Independent of one another: a list can have both a problem only a
  // person can fix and one the button fixes, and the button still
  // belongs on screen for its half.
  const action = staleAction(stale, doc.stale_remedies);
  /** Releases the optimistic order once the server has the same one. */
  const settle = { onSettled: () => setDraft(null) };

  const onDragStart = (event: DragStartEvent) => setDragging(String(event.active.id));

  /**
   * Which droppables a drag may land on.
   *
   * The default considers everything at once, and on this page that
   * produced holes: a whole-day list of blocks, their entries and the
   * parts containing both are all droppables, so a block hovering over
   * somebody's entry row resolved to a target it could never accept and
   * the drop went nowhere. Three rules fix it —
   *
   *   - an entry only ever sees its OWN block's rows, which is the
   *     no-split invariant expressed as "there is nowhere else to aim",
   *     rather than as a rejection after the fact
   *   - a block sees blocks and parts, never entries
   *   - a block hit beats the part containing it, so hovering a row
   *     inserts beside it and only genuinely empty space in a part drops
   *     to the end of that part
   *
   * The `closestCenter` tail is what removes the last dead zone: past
   * the end of the list, in a card's padding, between two parts — the
   * drag still resolves to the nearest thing it could sit next to
   * instead of silently refusing.
   */
  const collisionDetection: CollisionDetection = (args) => {
    const activeId = String(args.active.id);

    if (activeId.startsWith("e:")) {
      const home = findItemOfEntry(days, activeId.slice(2))?.id;
      const siblings = args.droppableContainers.filter((c) => {
        const id = String(c.id);
        return (
          id.startsWith("e:") &&
          findItemOfEntry(days, id.slice(2))?.id === home
        );
      });
      return closestCenter({ ...args, droppableContainers: siblings });
    }

    // Only a discipline block may enter a lane (R19): a manual block or
    // a whole skupina drah sees parts and the part's own items only.
    const dragged = findItem(days, activeId.slice(2));
    const intoLanes = dragged?.kind === "discipline";
    const inLane = (id: string) => {
      const c = id.startsWith("i:") ? containerOfItem(days, id.slice(2)) : undefined;
      return c?.kind === "lane";
    };
    const pool = args.droppableContainers.filter((c) => {
      const id = String(c.id);
      if (id.startsWith("l:")) return intoLanes;
      if (id.startsWith("i:")) return intoLanes || !inLane(id);
      return id.startsWith("s:");
    });
    const hits = pointerWithin({ ...args, droppableContainers: pool });
    const under = hits.length
      ? hits
      : rectIntersection({ ...args, droppableContainers: pool });

    // The innermost target wins: a block in a lane, then the lane, then a
    // block of the part (a skupina drah included), then the part itself.
    const pick = (test: (id: string) => boolean) => under.find((h) => test(String(h.id)));
    const best =
      pick((id) => id.startsWith("i:") && inLane(id)) ??
      pick((id) => id.startsWith("l:")) ??
      pick((id) => id.startsWith("i:")) ??
      pick((id) => id.startsWith("s:"));
    if (best) return [best];

    return closestCenter({
      ...args,
      droppableContainers: pool.filter((c) => String(c.id).startsWith("i:")),
    });
  };

  /**
   * Moves a block into the part it is hovering over, as it hovers.
   *
   * This is what "makes space": the receiving part re-renders with the
   * block already in it, so dnd-kit's sortable strategy shifts the rows
   * below out of the way instead of the block hanging over a list that
   * has not acknowledged it.
   */
  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      setDropHint(null);
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);
    // Entries never leave their block, so there is nothing to preview.
    if (!activeId.startsWith("i:")) return;

    const itemId = activeId.slice(2);
    const from = containerOfItem(days, itemId);
    if (!from) return;

    const to = overId.startsWith("i:")
      ? containerOfItem(days, overId.slice(2))
      : parseContainer(overId);
    if (!to || containerKey(to) === containerKey(from)) {
      setDropHint(null);
      return;
    }

    const item = findItem(days, itemId);
    if (!item) return;
    if (to.kind === "lane" && item.kind !== "discipline") {
      setDropHint(null);
      return;
    }
    // No live preview into or out of a lane: moving the block reflows the
    // lanes and the part under the pointer, the next hover lands somewhere
    // else, and the block either bounced straight back or was dropped by
    // whatever ended up under the cursor. The drop decides, against a
    // layout that did not move (onDragEnd).
    if (from.kind === "lane" || to.kind === "lane") {
      // A marker instead: a line where the block will land, drawn without
      // moving anything, so the pointer stays over what it is aiming at.
      const before = overId.startsWith("i:") && overId.slice(2) !== itemId ? overId.slice(2) : undefined;
      setDropHint({ key: containerKey(to), before });
      // The marker is the whole story: a block previewed in another part
      // a moment ago goes back to where it came from, rather than showing
      // in two places.
      setDraft(null);
      return;
    }
    setDropHint(null);
    const target = containerItems(days, to) ?? [];
    const index = overId.startsWith("i:")
      ? target.findIndex((i) => i.id === overId.slice(2))
      : target.length;

    const without = withContainerItems(
      days,
      from,
      (containerItems(days, from) ?? []).filter((i) => i.id !== itemId),
    );
    const next = target.filter((i) => i.id !== itemId);
    const at = index < 0 ? next.length : index;
    setDraft(withContainerItems(without, to, [...next.slice(0, at), item, ...next.slice(at)]));
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDragging(null);
    setDropHint(null);
    if (!over) {
      setDraft(null);
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);
    // Ids are namespaced so one DndContext can host both levels without
    // an entry ever landing among the blocks.
    if (activeId.startsWith("e:")) {
      if (!overId.startsWith("e:")) return;
      const entryId = activeId.slice(2);
      const item = findItemOfEntry(days, entryId);
      const overItem = findItemOfEntry(days, overId.slice(2));
      // Refused server-side too; bailing here just avoids the round trip.
      if (!item || !overItem || item.id !== overItem.id) return;
      const ids = (item.entries ?? []).map((e) => e.id);
      const next = arrayMove(ids, ids.indexOf(entryId), ids.indexOf(overId.slice(2)));
      if (sameOrder(ids, next)) return;
      setDraft(
        mapItems(days, (i) =>
          i.id === item.id
            ? { ...i, entries: next.map((eid) => item.entries!.find((e) => e.id === eid)!) }
            : i,
        ),
      );
      reorderEntries.mutate(
        { itemId: item.id, ids: next },
        { ...settle, onError: (err) => notifyError(err) },
      );
      return;
    }

    if (!activeId.startsWith("i:")) return;
    const itemId = activeId.slice(2);
    // onDragOver has usually moved it into the receiving container already;
    // when it has not (out of a lane, see there), the drop target decides.
    const current = containerOfItem(days, itemId);
    if (!current) return;
    const dragged = findItem(days, itemId);
    let dropped: Container | undefined;
    if (overId.startsWith("i:") && overId.slice(2) !== itemId) {
      dropped = containerOfItem(days, overId.slice(2));
    } else if (!overId.startsWith("i:")) {
      dropped = parseContainer(overId);
    }
    if (dropped && dropped.kind === "lane" && dragged?.kind !== "discipline") dropped = undefined;
    if (dropped && dragged && containerKey(dropped) !== containerKey(current)) {
      const into = (containerItems(days, dropped) ?? []).filter((i) => i.id !== itemId);
      const overIndex = overId.startsWith("i:") ? into.findIndex((i) => i.id === overId.slice(2)) : -1;
      const at = overIndex < 0 ? into.length : overIndex;
      const placed = [...into.slice(0, at), dragged, ...into.slice(at)];
      const without = withContainerItems(
        days,
        current,
        (containerItems(days, current) ?? []).filter((i) => i.id !== itemId),
      );
      setDraft(withContainerItems(without, dropped, placed));
      const nextIds = placed.map((i) => i.id);
      const doneMove = { ...settle, onError: (err: unknown) => notifyError(err) };
      if (dropped.kind === "lane") {
        reorderLane.mutate({ groupId: dropped.groupId, lane: dropped.lane, ids: nextIds }, doneMove);
      } else {
        reorderItems.mutate({ sectionId: dropped.sectionId, ids: nextIds }, doneMove);
      }
      return;
    }
    const container = current;
    const items = containerItems(days, container) ?? [];
    const ids = items.map((i) => i.id);
    const from = ids.indexOf(itemId);
    // Dropped ON a block of this container: take that block's place.
    // Dropped on the container itself — its empty space, past the last
    // row — go to the end.
    const overItem = overId.startsWith("i:") ? overId.slice(2) : "";
    const at = overItem && ids.includes(overItem) ? ids.indexOf(overItem) : -1;
    const next = arrayMove(ids, from, at < 0 ? ids.length - 1 : at);

    const original = containerOfItem(doc.days, itemId);
    const originalIds = original ? (containerItems(doc.days, original) ?? []).map((i) => i.id) : [];
    if (original && containerKey(original) === containerKey(container) && sameOrder(originalIds, next)) {
      setDraft(null);
      return;
    }
    setDraft(
      withContainerItems(
        days,
        container,
        next.map((iid) => items.find((i) => i.id === iid)!),
      ),
    );
    const done = { ...settle, onError: (err: unknown) => notifyError(err) };
    if (container.kind === "lane") {
      reorderLane.mutate({ groupId: container.groupId, lane: container.lane, ids: next }, done);
    } else {
      reorderItems.mutate({ sectionId: container.sectionId, ids: next }, done);
    }
  };

  const onBlockSubmit = (payload: BlockPayload) => {
    if (modal.kind !== "block") return;
    const done = {
      onSuccess: () => {
        setModal({ kind: "closed" });
        notifySuccess(modal.item ? "Blok upraven" : "Blok vložen");
      },
      onError: (err: unknown) => notifyError(err),
    };
    if (modal.item) {
      updateBlock.mutate({ itemId: modal.item.id, ...payload }, done);
    } else {
      insertBlock.mutate(
        { sectionId: modal.sectionId, after_item_id: modal.afterItemId, ...payload },
        done,
      );
    }
  };

  // Publishing a running order while entries are still open leaves the
  // door open behind the document: a club can still enter, and the paper
  // the hall already holds no longer describes the field. The organizer
  // is asked rather than overruled — a provisional order published days
  // ahead of the uzávěrka is a legitimate thing to do — and the answer
  // rides in the same request, so the list and the deadline cannot end up
  // disagreeing.
  const onPublish = () =>
    publish.mutate(
      // Gated on `entriesStillOpen` as well as on the tick, because the
      // tick's own control is: the checkbox renders only inside the alert
      // that appears when entries are open, and it defaults to true. With
      // the competition cached for 30 s, a deadline extended in another
      // tab (or by another organizer) leaves a dialog with no checkbox
      // that would still have sent close_entries — silently shutting a
      // door somebody had just reopened. The server cannot catch it: from
      // its side that is exactly a legitimate close.
      { close_entries: entriesStillOpen && closeEntries },
      {
        onSuccess: (res) => {
          setPublishOpen(false);
          notifySuccess(
            res.entries_closed
              ? `Zveřejněno a přihlášky uzavřeny. ${fixedNumbers(res.numbers)}`
              : `Zveřejněno. ${fixedNumbers(res.numbers)}`,
          );
        },
        onError: (err) => notifyError(err),
      },
    );

  // The organizer unlocked THIS publication; a republish locks again.
  const editUnlocked = doc.published && unlockedFor === (doc.published_at?.valueOf() ?? 0);

  const handlers: RowHandlers = {
    view,
    onScratch: (entryRowId, scratched) =>
      scratch.mutate(
        { entryRowId, scratched },
        { onError: (err) => notifyError(err) },
      ),
    onOpenDetail: (competitionEntryID) => setDetailEntryId(competitionEntryID),
    onEditBlock: (item, sectionId) => setModal({ kind: "block", sectionId, item }),
    // Asked for, like removing a část below. The button that reaches this
    // sits in the non-discipline branch of ItemRows, so what it deletes is a
    // prezence / přestávka / vyhlášení: it holds no přihlášky, and „Aktualizovat"
    // will not bring it back — the organizer re-adds it and drags it into place.
    onDeleteBlock: (itemId, name) =>
      confirm({
        title: "Odebrat blok",
        description: (
          <>
            {/* The old second half („nový se přidá na konec části") stopped
                being true when the insertion slots landed: a new block goes
                wherever the organizer drops it, including the top. */}
            Blok <strong>{name}</strong> zmizí z listiny a časy pod ním se posunou.
            Vrátit ho jde jen ručně.
          </>
        ),
        confirmLabel: "Odebrat",
        variant: "danger",
        onConfirm: () =>
          deleteBlock.mutateAsync(itemId).then(() => {
            notifySuccess("Blok odebrán");
          }),
      }),
    onInsertAt: (sectionId, afterItemId) =>
      setModal({ kind: "block", sectionId, afterItemId }),
    onAddLanes: (sectionId) =>
      createLaneGroup.mutate(
        { sectionId, lane_mode: "parallel", lane_count: 2 },
        {
          onSuccess: () => notifySuccess("Dráhy přidány. Přetáhněte do nich kategorie."),
          onError: (err) => notifyError(err),
        },
      ),
    onLaneGroupChange: (itemId, mode, count) =>
      updateLaneGroup.mutate(
        { itemId, lane_mode: mode, lane_count: count },
        { onError: (err) => notifyError(err) },
      ),
    onDissolveLanes: (itemId) =>
      confirm({
        title: "Zrušit dráhy",
        description:
          "Kategorie z drah se vrátí do části na místo skupiny, dráha po dráze. Startovní čísla zůstanou.",
        confirmLabel: "Zrušit dráhy",
        cancelLabel: "Ne",
        variant: "warning",
        onConfirm: () =>
          deleteBlock.mutateAsync(itemId).then(() => {
            notifySuccess("Dráhy zrušeny");
          }),
      }),
    onToggleSelect: selection.toggle,
    // No explicit on/off: toggleMany without it flips the scope the way the
    // buttons want — clears when every block in the day or part is already
    // ticked, ticks them all otherwise. The caller used to compute that
    // itself and pass `!allPicked`.
    onSelectMany: selection.toggleMany,
    selected,
    selectable: true,
    // An entry never leaves its own block, so while one is in hand the
    // rest of the day is not a possible destination — dimming says that
    // before the drop does, instead of leaving the organizer to discover
    // it by having nothing happen.
    confinedTo:
      dragging?.startsWith("e:")
        ? (findItemOfEntry(days, dragging.slice(2))?.id ?? null)
        : null,
    movingBlock: dragging?.startsWith("i:") ?? false,
    dropHint,
    // A published list takes edits (R16), but only once the organizer has
    // said so: the gate is this screen's alone, nothing is stored.
    locked: frozen || access === "elsewhere" || (doc.published && !editUnlocked),
    structureLocked: running,
    frozen: frozen || access === "elsewhere",
  };

  // Selection is by BLOCK, not by category: „přesunout" needs the block
  // and „sloučit" needs the category behind it, and one of those cannot
  // be recovered from the other — a category may not be on the list yet.
  //
  // Everything below is derived from the DOCUMENT, never from `selected`
  // directly. A rebuild, a merge or another organizer can delete a block
  // while it is ticked, and the raw array then holds ids of things that
  // no longer exist: the count said „Vybráno 3" for two blocks, and the
  // move posted the dead id along with the live ones.
  const pickedItems = selected
    .map((itemId) => findItem(days, itemId))
    .filter((i): i is StartListItem => !!i);
  const pickedIds = pickedItems.map((i) => i.id);
  // Deduplicated: in the `category_split` state one category legitimately
  // owns two blocks, and ticking both used to offer „Sloučit" on a
  // category with itself — the organizer's most natural reaction to
  // seeing two halves, answered with a server error instead of a nudge
  // towards „Aktualizovat".
  const pickedCategories = [
    ...new Set(
      pickedItems.map((i) => i.competition_category_id).filter((id): id is ID => id != null),
    ),
  ];

  /** Appends the picked blocks to the end of a part, in the order picked. */
  const moveSelected = (sectionId: ID) => {
    const target = findSection(days, sectionId);
    if (!target) return;
    if (pickedIds.length === 0) return;
    const staying = target.items.map((i) => i.id).filter((id) => !pickedIds.includes(id));
    reorderItems.mutate(
      { sectionId, ids: [...staying, ...pickedIds] },
      {
        onSuccess: () => {
          selection.clear();
          notifySuccess(
            pickedIds.length === 1
              ? `Kategorie přesunuta do „${target.name}“`
              : `${pickedIds.length} ${pluralizeCzech(pickedIds.length, [
                  "kategorie přesunuta",
                  "kategorie přesunuty",
                  "kategorií přesunuto",
                ])} do „${target.name}“`,
          );
        },
        onError: (err) => notifyError(err),
      },
    );
  };

  const dragged = dragging ? findDragged(days, dragging) : null;

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title={doc.name}
          subtitle={doc.competition_name}
          actions={
            <>
              {/* The same document a leader opens, in the same renderer —
                  so "what will they see" is answered by showing it, not
                  by describing it. */}
              <Button onClick={() => window.open(`/soutez/${compId}/startovni-listina/tisk`)}>
                Náhled
              </Button>
              {!frozen && !running && (
                <Button
                  onClick={onBuild}
                  loading={build.isPending}
                  variant={isEmpty ? "primary" : undefined}
                >
                  {isEmpty ? FIRST_BUILD_LABEL : ACTION_LABEL}
                </Button>
              )}
              {!frozen && doc.stage === "draft" && (
                // A draft for the clubs to see: numbers still follow the
                // order and nobody is mailed (R16).
                <Button
                  loading={publishDraft.isPending}
                  onClick={() =>
                    confirm({
                      title: "Zveřejnit návrh startovní listiny?",
                      confirmLabel: "Ano, zveřejnit návrh",
                      cancelLabel: "Ne",
                      description:
                        "Listinu uvidí všechny týmy jako návrh, zatím bez startovních čísel. Týmy nedostanou e-mail.",
                      onConfirm: () =>
                        publishDraft.mutateAsync().then(() => {
                          notifySuccess("Návrh zveřejněn. Týmy listinu vidí.");
                        }),
                    })
                  }
                >
                  {PUBLISH_DRAFT_LABEL}
                </Button>
              )}
              {/* While the hall runs it, the list is the hall's: nothing
                  here could be edited once unlocked. */}
              {!frozen && access !== "elsewhere" && doc.published && !editUnlocked && (
                <Button
                  startIcon={<Pencil aria-hidden="true" size={15} />}
                  onClick={() =>
                    confirm({
                      title: EDIT_PUBLISHED_LABEL,
                      confirmLabel: "Ano, upravit",
                      cancelLabel: "Ne",
                      variant: "warning",
                      children: (
                        <>
                          <Text>
                            Týmy listinu už mají, možná i vytištěnou. Každá změna pořadí nebo
                            časů se jim ukáže hned.
                          </Text>
                          <Text>
                            Startovní čísla zůstanou, jak jsou: přesunutý start si své číslo
                            nese s sebou.
                          </Text>
                        </>
                      ),
                      onConfirm: async () => {
                        setUnlockedFor(doc.published_at?.valueOf() ?? 0);
                      },
                    })
                  }
                >
                  {EDIT_PUBLISHED_LABEL}
                </Button>
              )}
              {!frozen && doc.stage !== "draft" && canUnpublish && (
                // Only before Probíhá: from then on the hall runs off this
                // list and its numbers, and the server refuses (plan §4).
                <Button
                  // Outline — the confirm behind it carries the solid one.
                  variant="danger"
                  weight="outline"
                  onClick={() =>
                    confirm({
                      title: UNPUBLISH_LABEL,
                      confirmLabel: "Ano, zrušit zveřejnění",
                      cancelLabel: "Ne",
                      // Reversible — the list can be republished. Not free
                      // either: from „zveřejněno" every start number is
                      // cleared, which is exactly what `warning` is for.
                      variant: "warning",
                      children: (
                        <>
                          {doc.published && (
                            <Alert variant="warn">
                              Startovní čísla se uvolní. Při dalším zveřejnění se přidělí
                              znovu podle aktuálního pořadí, takže se mohou lišit od těch,
                              které týmy už mají. Hudba, kterou si zvukař stáhl, ani
                              vytištěné listiny pak nebudou sedět.
                            </Alert>
                          )}
                          <Text>Listina zmizí týmům ze zobrazení a bude zase rozpracovaná.</Text>
                        </>
                      ),
                      onConfirm: () =>
                        unpublish.mutateAsync().then(() => {
                          notifySuccess("Zveřejnění zrušeno. Listina je zase rozpracovaná.");
                        }),
                    })
                  }
                >
                  {UNPUBLISH_LABEL}
                </Button>
              )}
              {!frozen && !doc.published && (
                <Button variant="primary" onClick={() => setPublishOpen(true)}>
                  Zveřejnit
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <Text variant="muted">
            Celkem <strong>{doc.entry_count}</strong>{" "}
            {pluralizeCzech(doc.entry_count, ["start", "starty", "startů"])} v{" "}
            <strong>{doc.category_count}</strong>{" "}
            {pluralizeCzech(doc.category_count, ["kategorii", "kategoriích", "kategoriích"])}
            {/* Scratched rows are inside entry_count — they keep their slot
                and their number — so the count is stated beside it rather
                than subtracted from it. The estimated finish is NOT here:
                each day carries its own, and a single number over a
                three-day event answered a question nobody asks. */}
            {doc.scratched_count > 0 && (
              <>
                {", z toho "}
                <strong>{doc.scratched_count}</strong> přeškrtnutých
              </>
            )}
            {" · "}
            <Badge variant={STAGE_VARIANT[doc.stage]}>{STAGE_LABELS[doc.stage]}</Badge>
          </Text>

          {(frozen || access === "elsewhere") && (
            <Alert variant="info">{lockedNotice(competition, readOnly)}</Alert>
          )}
          {competition?.status === "running" && delayMinutes(doc) > 0 && (
            <Alert variant="warn">
              Soutěž má teď skluz {delayMinutes(doc)} min. Předpokládané časy
              jsou u startů za znakem ≈; zveřejněné časy se nemění.
            </Alert>
          )}
          {running && !readOnly && (
            <Alert variant="info">
              Soutěž probíhá: změny listiny jdou živým provozem na všechna
              zařízení. Den, který už začal, si nechává začátek i části;
              Aktualizovat a zveřejnění jsou do konce soutěže uzavřené.
            </Alert>
          )}
          {!frozen && doc.stage === "preview" && (
            <Alert variant="info">
              Týmy listinu vidí jako návrh, zatím bez startovních čísel. Ta uvidí po
              zveřejnění.
            </Alert>
          )}
          {!frozen && editUnlocked && (
            <Alert variant="warn">
              Upravujete zveřejněnou listinu. Týmy každou změnu uvidí hned; startovní čísla se
              nemění.
            </Alert>
          )}

          {/* One banner for every way the list has drifted from what it was
              built out of. A start list is derived but not live — arranged
              by hand once and then left alone — so an edit to the
              competition underneath it leaves a document that quietly
              disagrees with its own source, and nothing else would say
              so. */}
          {stale.length > 0 && (
            <Alert variant="warn">
              <strong>{staleTitle(stale)}</strong>
              {/* One line per drift, each a complete sentence, then ONE call
                  to action. Run together as flowing text they collided
                  without punctuation, and a manual reason among them left
                  „Doplňte je tlačítkem" pointing at no button at all. */}
              <ul className={styles.staleList}>
                {stale.map((reason) => {
                  const copy = staleCopy(reason);
                  return copy ? <li key={reason}>{copy.text}</li> : null;
                })}
              </ul>
              {/* Not disabled on a published list. The server builds one
                  quite happily — a late entry draws the next free start
                  number, which is what a paper start list with a late
                  entry does — and the identical button in the header a row
                  above was never disabled. The banner disabling it meant
                  „Doplňte je tlačítkem" pointed at a control that would
                  not move: the same broken promise as a reason no button
                  fixes. */}
              {action && !frozen && !running && (
                <p className={styles.staleText}>
                  {action}{" "}
                  <Button
                    size="sm"
                    variant="primary"
                    loading={build.isPending}
                    onClick={onBuild}
                  >
                    {ACTION_LABEL}
                  </Button>
                </p>
              )}
            </Alert>
          )}

          {/* Rearranging a day means dragging blocks past hundreds of rows
              that are not the subject of the move, so the running order is
              also available on its own. One setting with two states, so a
              switch rather than a tab strip — there is no second view to
              navigate to, just detail to turn off. */}
          <Switch
            checked={view === "compact"}
            onChange={(e) => setView(e.target.checked ? "compact" : "entries")}
            label="Pouze názvy kategorií"
          />

          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            // The drag itself moves blocks between parts, so every rect on
            // the page changes mid-drag. Measured once, the rects go stale
            // after the first cross-part move and the rest of the drag
            // lands on nothing.
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={() => {
              setDragging(null);
              setDropHint(null);
              setDraft(null);
            }}
          >
            {days.map((day) => (
              <DayCard
                key={day.id}
                day={day}
                multiDay={days.length > 1}
                handlers={handlers}
                onAddSection={(name) =>
                  addSection.mutate(
                    { dayId: day.id, name },
                    {
                      onSuccess: () => notifySuccess("Část přidána"),
                      onError: (err) => notifyError(err),
                    },
                  )
                }
                onRenameSection={(sectionId, name) =>
                  renameSection.mutate(
                    { sectionId, name },
                    { onError: (err) => notifyError(err) },
                  )
                }
                onDeleteSection={async (sectionId) => {
                  try {
                    await deleteSection.mutateAsync(sectionId);
                    notifySuccess("Část odebrána, obsah připojen k sousední");
                  } catch (err) {
                    notifyError(err);
                  }
                }}
                onJoinDay={async (sectionIds) => {
                  // One at a time. Firing them together only decided the
                  // SEND order — the requests then raced on the server, so
                  // a day of three or more parts could splice its contents
                  // in an order nobody chose, and every one of them
                  // reported success separately.
                  try {
                    for (const id of sectionIds) {
                      await deleteSection.mutateAsync(id);
                    }
                    notifySuccess("Části spojeny");
                  } catch (err) {
                    notifyError(err);
                  }
                }}
                onSetStart={(seconds) =>
                  updateDay.mutate(
                    { dayId: day.id, start_seconds: seconds },
                    { onError: (err) => notifyError(err) },
                  )
                }
              />
            ))}

            {/* The dragged row travels with the cursor instead of leaving
                a hole where it used to be. No drop animation: a block moved
                into or out of a lane is re-mounted elsewhere, and while the
                overlay flew to it the next drag would not start. */}
            <DragOverlay dropAnimation={null}>
              {dragged && <div className={styles.overlay}>{dragged}</div>}
            </DragOverlay>
          </DndContext>

          {/* Sticky at the foot of the list it acts on. The library owns the
              geometry now — this used to be `position: fixed` with `top`
              computed from the topbar's height and `left` from the sidebar's
              width, which meant a page stylesheet had to know the shell's
              measurements and re-check them after every bump.

              Gone while the rows are locked, along with the ticks that fill
              it: `selected` survives a publish, and a bar asking only whether
              anything was ticked would offer moves on a list the organizer
              has not unlocked. „Sloučit" goes for good once the list is
              published: the server refuses a merge from then on (R16). */}
          {!handlers.locked && (
            <BulkActionBar
              count={pickedIds.length}
              onClear={selection.clear}
              nounForms={["kategorie", "kategorie", "kategorií"]}
            >
              <Select
                aria-label="Přesunout vybrané do části"
                value=""
                onChange={(e) => e.target.value && moveSelected(e.target.value)}
              >
                <option value="">Přesunout do…</option>
                {days.map((day) => (
                  <optgroup
                    key={day.id}
                    label={
                      days.length > 1
                        ? `${formatWeekday(day.date)} ${formatDate(day.date)}`
                        : formatDate(day.date)
                    }
                  >
                    {day.sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
              {!doc.published && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={pickedCategories.length < 2}
                  onClick={() => setMergeOpen(true)}
                >
                  Sloučit
                </Button>
              )}
            </BulkActionBar>
          )}
        </Page.Content>
      </Page>

      <BlockModal
        open={modal.kind === "block"}
        item={modal.kind === "block" ? modal.item : undefined}
        pending={insertBlock.isPending || updateBlock.isPending}
        error={insertBlock.error ?? updateBlock.error}
        onClose={() => setModal({ kind: "closed" })}
        onSubmit={onBlockSubmit}
      />

      {/* Publishing freezes the numbers and puts the day in front of every
          club, so it asks first. A summary table said nothing the page
          behind the dialog was not already showing; the one thing worth
          interrupting for is the warning. */}
      <ConfirmDialog
        open={publishOpen}
        title="Zveřejnit startovní listinu?"
        confirmLabel="Ano, zveřejnit"
        cancelLabel="Ne"
        busy={publish.isPending}
        // Not `busy`: that means "already sending" and draws a spinner. This
        // is "you may not proceed", and the Alert below says why.
        confirmDisabled={hasOrphanDay}
        onClose={() => setPublishOpen(false)}
        onConfirm={onPublish}
      >
        {/* The server refuses this one (409), so the dialog says why
            rather than letting the press fail. The day cannot be moved
            for them — which surviving day a Saturday afternoon belongs
            on is a decision — and the club and print views do not show
            the flag at all, so a published list with such a day would
            reach the hall with nothing saying so. */}
        {hasOrphanDay && (
          <Alert variant="err">
            Listina má den, kdy se soutěž nekoná, a je v něm rozvržený program. Přesuňte z
            něj kategorie na den, kdy se soutěž koná, a teprve pak zveřejněte.
          </Alert>
        )}
        {/* Entries still open behind the document. Not a refusal: the
            organizer may want a provisional order out early. But left
            unsaid, the paper the hall holds stops describing the field
            the moment the next club enters. */}
        {/* The checkbox sits in a block of its own, not inside the alert.
            An alert states a fact; this asks for a decision, and a control
            buried in warning-coloured prose reads as part of the warning
            rather than as something to answer. The block tints only when
            it is ticked, so the dialog shows at a glance which of the two
            acts is about to happen. */}
        {entriesStillOpen && (
          <>
            <Alert variant="warn">
              Přihlášky jsou stále otevřené
              {competition?.entry_deadline
                ? ` do ${formatDateTime(competition.entry_deadline)}`
                : ""}
              . Zveřejněná listina se pak nebude shodovat s tím, kdo je přihlášený.
            </Alert>
            <div
              className={closeEntries ? styles.publishOptOn : styles.publishOpt}
              data-armed={closeEntries ? "1" : undefined}
            >
              <Checkbox
                label="Zároveň uzavřít přihlášky"
                checked={closeEntries}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCloseEntries(e.target.checked)
                }
              />
              <p className={styles.publishOptNote}>
                {closeEntries
                  ? "Uzávěrka se posune na teď a další tým se už nepřihlásí. Uzávěrka hudby i uzávěrky změn zůstanou, kde jsou."
                  : "Bez zaškrtnutí zůstávají přihlášky otevřené a listina se může rozejít s tím, kdo je přihlášený."}
              </p>
            </div>
          </>
        )}
        {/* A separate fact from the one above, and it survives „Zároveň
            uzavřít přihlášky": that moves the uzávěrka přihlášek and
            deliberately leaves the uzávěrky změn where they are.
            Publishing shuts them anyway, for everybody. */}
        {changesStillOpen && competition?.effective_entry_roster_deadline && (
          <Alert variant="warn">
            Uzávěrka změn soutěžících je až{" "}
            {formatDateTime(competition.effective_entry_roster_deadline)}. Zveřejněním se
            týmům uzavřou změny přihlášek hned; po zveřejnění mění přihlášky jen
            organizátor soutěže.
          </Alert>
        )}
        {doc.unplaced_entries > 0 && (
          <Alert variant="warn">
            {doc.unplaced_entries === 1
              ? "Jedna přihláška ještě není v listině a zveřejněním se na ni nedostane."
              : `${doc.unplaced_entries} ${pluralizeCzech(doc.unplaced_entries, [
                  "přihláška ještě není",
                  "přihlášky ještě nejsou",
                  "přihlášek ještě není",
                ])} v listině a zveřejněním se na ně nedostane.`}{" "}
            Nejdřív použijte „{ACTION_LABEL}“.
          </Alert>
        )}
        <Text>
          Startovní čísla se zafixují a od té chvíle se nemění, ani přeskládáním. Listinu
          uvidí všechny týmy, dostanou o ní e-mail a zvukař si může stáhnout hudbu.
        </Text>
      </ConfirmDialog>

      {/* Unpublishing releases the numbers, which no button label can
          convey — and it is the half of the operation somebody has to be
          told about, because a number a club already wrote down may come
          back different. */}

      {detailEntryId !== null && (
        <EntryDetailModal
          entry={detailEntry}
          combo={
            detailEntry
              ? ruleIndex?.combos.get(detailEntry.discipline_category_id)
              : undefined
          }
          // The organizer's own page: no uzávěrka binds them, and the
          // grace band may be named.
          canUploadMusic={!frozen}
          audience="organizer"
          onClose={() => setDetailEntryId(null)}
        />
      )}

      <MergeCategoriesModal
        open={mergeOpen}
        competitionId={compId}
        categoryIds={pickedCategories}
        onClose={() => setMergeOpen(false)}
        onMerged={(row) => {
          setMergeOpen(false);
          selection.clear();
          notifySuccess(`Sloučeno do „${row.name}“. Stiskněte „${ACTION_LABEL}“, aby se blok přestavěl.`);
        }}
      />
    </>
  );
}

interface RowHandlers {
  view: View;
  onScratch: (entryRowId: ID, scratched: boolean) => void;
  /** Opens the přihláška behind a row. Takes the COMPETITION ENTRY id,
   * not the start-list row id: the dialog is about the entry, which
   * outlives any one running order. */
  onOpenDetail: (competitionEntryID: ID) => void;
  onEditBlock: (item: StartListItem, sectionId: ID) => void;
  onDeleteBlock: (itemId: ID, name: string) => void;
  onInsertAt: (sectionId: ID, afterItemId?: ID) => void;
  /** A new skupina drah at the top of a part (R19). */
  onAddLanes: (sectionId: ID) => void;
  onLaneGroupChange: (itemId: ID, mode: StartListLaneMode, count: number) => void;
  /** Dissolves a group: its blocks go back to the part, lane by lane. */
  onDissolveLanes: (itemId: ID) => void;
  onToggleSelect: (item: StartListItem) => void;
  /** Ticks or clears a whole day's or part's worth of blocks at once. */
  onSelectMany: (items: StartListItem[]) => void;
  selected: ID[];
  /** The overlay renders a picture of a row, so it takes no checkbox. */
  selectable: boolean;
  /** While an entry is being dragged, the block it belongs to — the only
   * place it can land. Every other block is dimmed whole. */
  confinedTo: ID | null;
  /** True while a whole discipline is in hand. The competitors recede so
   * the headings — the things the block can land between — stand out;
   * the headings themselves stay lit, because they are the targets. */
  movingBlock: boolean;
  /** Structure edits are off: a frozen competition, or a published list
   * the organizer has not unlocked („Upravit zveřejněnou listinu"). */
  locked: boolean;
  /** Probíhá: a day that has begun keeps its start time and parts; one
   * nobody has started may still change them (decided 20. 10. 2026). */
  structureLocked: boolean;
  /** Dokončeno or archived, or run by the hall: not even přeškrtnutí. */
  frozen: boolean;
  /** Where a block crossing into or out of a lane will land. */
  dropHint?: DropHint | null;
  /** A picture in the DragOverlay. It must register no droppable or
   * sortable: the overlay shares the DndContext, so a lane drawn there
   * would take over the real lane's id and unregister it on drop. */
  overlay?: boolean;
}

function DayCard({
  day,
  multiDay,
  handlers,
  onAddSection,
  onRenameSection,
  onDeleteSection,
  onJoinDay,
  onSetStart,
}: {
  day: StartListDay;
  multiDay: boolean;
  handlers: RowHandlers;
  onAddSection: (name: string) => void;
  onRenameSection: (sectionId: ID, name: string) => void;
  onDeleteSection: (sectionId: ID) => void | Promise<void>;
  /** Absorbs every part but the first, in order. See the caller. */
  onJoinDay: (sectionIds: ID[]) => void | Promise<void>;
  onSetStart: (seconds: number) => void;
}) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const settled = handlers.locked || (handlers.structureLocked && !!day.started);

  // Every discipline the day runs, across all of its parts. An undivided
  // day has no part header to hang „Vybrat vše" on, and a divided one
  // still wants the whole-day answer — „všechno v neděli jde jinam" is
  // one decision, not one per part.
  const blocks = day.sections.flatMap((sec) =>
    // The day's own blocks, not those in a skupina drah: moving a whole
    // day or part elsewhere must not pull blocks out of their lanes. One
    // ticked by hand still moves, and leaves its lane.
    sec.items.filter((i) => i.kind === "discipline"),
  );
  // Still computed, but only for the label: toggleMany works the flip out
  // for itself.
  const allPicked =
    blocks.length > 0 && blocks.every((i) => handlers.selected.includes(i.id));

  // A day is divided or it is not. One part means undivided — the part
  // still exists underneath, because everything has to live somewhere,
  // but the organizer never sees it and never names it.
  const divided = day.sections.length > 1;

  const join = () =>
    confirm({
      title: "Spojit části dne?",
      description: (
        <>
          Kategorie a bloky zůstanou; přejdou do jedné souvislé části v pořadí, v jakém
          jsou teď. Zmizí jen rozdělení a názvy částí. Rozdělit den znovu znamená části
          pojmenovat a kategorie přetáhnout zpátky.
        </>
      ),
      confirmLabel: "Spojit",
      // Returned, not fired and forgotten: a day of four parts is three
      // sequential requests, and the dialog holds its spinner until they are
      // through. onJoinDay reports its own failure.
      onConfirm: () =>
        // Backwards, so each part is absorbed by the one before it and the
        // running order comes out in the order it was already in. The caller
        // walks them one at a time — see onJoinDay.
        onJoinDay([...day.sections].slice(1).reverse().map((s) => s.id)),
    });

  return (
    <section className={styles.day}>
      <div className={styles.dayHead}>
        <h2 className={styles.dayTitle}>
          {multiDay
            ? `${formatWeekday(day.date)} ${formatDate(day.date)}`
            : formatDate(day.date)}
          {/* The competition stopped running on this day but its schedule
              is still here. Saying so on the day itself is what makes the
              instruction in the banner actionable — otherwise "move the
              blocks off it" leaves you hunting for which day it means. */}
          {day.outside_competition && <Badge variant="warn">Mimo termín soutěže</Badge>}
          {/* Only the end. The start is the editable field on this very
              row and the length is the difference between two numbers
              already on screen — a hall is booked by the hour, and what
              the header has to add is when the day is expected to
              finish. Resolved from the server's fold. */}
          <span className={styles.dayClock}>konec {formatTime(day.end_at)}</span>
        </h2>
        {/* The day's own settings live behind this, not along the header:
            they are set once when the event is planned and then never
            touched, so a permanent row of controls over every day was
            three widgets of noise per day for a document you mostly
            read. */}
        <span className={styles.dayActions}>
          {handlers.selectable && !handlers.locked && blocks.length > 0 && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => handlers.onSelectMany(blocks)}
            >
              {allPicked ? "Odznačit vše" : "Vybrat vše"}
            </Button>
          )}
          {/* An undivided day has no part header, so its one part's
              „Přidat dráhy" lives here. */}
          {!divided && handlers.selectable && !handlers.locked && day.sections[0] && (
            <Button size="sm" onClick={() => handlers.onAddLanes(day.sections[0].id)}>
              Přidat dráhy
            </Button>
          )}
          {divided && (
            <Button
              size="sm"
              disabled={settled}
              onClick={() => onAddSection(`${day.sections.length + 1}. část`)}
            >
              Přidat část
            </Button>
          )}
          <Button
            iconOnly
            size="sm"
            aria-label={`Nastavení dne ${day.date}`}
            disabled={settled}
            onClick={() => setEditing(true)}
            startIcon={<Pencil aria-hidden="true" size={16} />}
          />
        </span>
      </div>

      {day.sections.map((section) => (
        <SectionCard
          key={section.id}
          section={section}
          handlers={handlers}
          // An undivided day shows no part chrome at all: the card IS the
          // day's running order, and „1. část“ over the only part is a
          // heading that says nothing.
          titled={divided}
          settled={settled}
          onRename={(name) => onRenameSection(section.id, name)}
          onDelete={() => onDeleteSection(section.id)}
        />
      ))}

      {editing && (
        <DaySettingsModal
          day={day}
          divided={divided}
          published={settled}
          onClose={() => setEditing(false)}
          onAddSection={onAddSection}
          onJoin={join}
          onSetStart={onSetStart}
        />
      )}

    </section>
  );
}

/**
 * One část, as its own card.
 *
 * The parts used to be a tinted strip inside the day's card, which read
 * as a heading rather than as the container it is — and a container is
 * exactly what an organizer needs to see when deciding where to drop a
 * discipline.
 */
/**
 * The day's own settings — its opening time, and whether it is divided.
 *
 * Rendered only while open, and that is the whole design. A form holds a
 * copy of the truth, and a copy is only trustworthy for as long as the
 * conversation that started it. This used to live in DayCard, which never
 * unmounts across refetches, so the copy outlived every conversation:
 * organizer A set the start to 08:00, B's page quietly refetched, and B's
 * input still said 07:00 — so B clicking into the field and clicking away
 * again, without typing a character, sent 07:00 and undid A's change.
 *
 * Mounting with the dialog makes the seed value the current one by
 * construction, so an untouched field cannot differ from the document and
 * the guard below has nothing to send.
 */
function DaySettingsModal({
  day,
  divided,
  published,
  onClose,
  onAddSection,
  onJoin,
  onSetStart,
}: {
  day: StartListDay;
  divided: boolean;
  published: boolean;
  onClose: () => void;
  onAddSection: (name: string) => void;
  onJoin: () => void;
  onSetStart: (seconds: number) => void;
}) {
  const [start, setStart] = useState(formatDayTime(day.start_seconds));

  const commitStart = () => {
    const [h, m] = start.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) {
      setStart(formatDayTime(day.start_seconds));
      return;
    }
    const seconds = h * 3600 + m * 60;
    if (seconds !== day.start_seconds) onSetStart(seconds);
  };

  return (
    <Modal
      open
      title={`Nastavení dne · ${formatWeekday(day.date)} ${formatDate(day.date)}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Hotovo</Button>}
    >
      <FormStack>
        <Input
          label="Začátek"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          onBlur={commitStart}
          className={styles.startInput}
          disabled={published}
        />
        <Switch
          checked={divided}
          disabled={published}
          onChange={(e) => (e.target.checked ? onAddSection("2. část") : onJoin())}
          // No hint: the lib's Switch wraps label AND hint in one
          // <label>, so a hint becomes part of the control's accessible
          // name — "Rozdělit na části" stops being what a screen reader
          // announces.
          label="Rozdělit na části"
        />
      </FormStack>
    </Modal>
  );
}

/** Renaming a part. Mounted only while open, for the reason above. */
function RenameSectionModal({
  section,
  onClose,
  onRename,
}: {
  section: StartListSection;
  onClose: () => void;
  onRename: (name: string) => void;
}) {
  const [name, setName] = useState(section.name);
  const trimmed = name.trim();

  const save = () => {
    if (trimmed && trimmed !== section.name) onRename(trimmed);
    onClose();
  };

  return (
    <FormModal
      open
      title="Přejmenovat část"
      submitLabel="Uložit"
      canSubmit={!!trimmed}
      onSubmit={save}
      onClose={onClose}
    >
      <Input
        label="Název části"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
    </FormModal>
  );
}

function SectionCard({
  section,
  handlers,
  titled,
  settled,
  onRename,
  onDelete,
}: {
  section: StartListSection;
  handlers: RowHandlers;
  titled: boolean;
  /** The day's parts may not change (locked, or a day that has begun). */
  settled: boolean;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  // The whole part is a drop target, so a block can be moved into one
  // that is still empty.
  const { setNodeRef, isOver } = useDroppable({ id: `s:${section.id}` });

  const confirm = useConfirm();
  const blocks = section.items.filter((i) => i.kind === "discipline");
  const allPicked =
    blocks.length > 0 && blocks.every((i) => handlers.selected.includes(i.id));
  const onSelectAll = () => handlers.onSelectMany(blocks);

  return (
    <Card className={isOver ? `${styles.section} ${styles.sectionOver}` : styles.section}>
      {titled ? (
        <Card.Header
          title={
            // The name is a heading you can edit, not a field you keep
            // tabbing through: a part is named once and then read all
            // day, so it reads as text and the pencil opens the rename.
            <span className={styles.sectionTitle}>
              {section.name}
              <Button
                iconOnly
                size="sm"
                weight="ghost"
                // The class carries only the reveal-on-approach behaviour
                // now; the button's own look is the lib's.
                className={styles.sectionEdit}
                aria-label={`Přejmenovat část ${section.name}`}
                disabled={settled}
                onClick={() => setRenaming(true)}
                startIcon={<Pencil aria-hidden="true" size={14} />}
              />
            </span>
          }
          actions={
            // Everything lives in one slot so the order is ours:
            // „Vybrat vše", then the clock, and the × last against the
            // edge where a dismiss belongs. Left in `subtitle` the
            // clock sat ahead of everything, which put a read-only
            // number where the first control should be.
            <span className={styles.sectionActions}>
              {/* Selecting a whole part is how a day gets rearranged:
                  "everything after lunch moves to Sunday" is one
                  decision, not fourteen clicks. A part does not move as
                  an object — what travels is the disciplines, and
                  moving the part left the old day holding an empty
                  part nobody asked for. */}
              {handlers.selectable && !handlers.locked && blocks.length > 0 && (
                <Button size="sm" variant="primary" onClick={onSelectAll}>
                  {allPicked ? "Odznačit vše" : "Vybrat vše"}
                </Button>
              )}
              {handlers.selectable && !handlers.locked && (
                <Button size="sm" onClick={() => handlers.onAddLanes(section.id)}>
                  Přidat dráhy
                </Button>
              )}
              <span className={styles.sectionClock}>
                {formatTime(section.start_at)} – {formatTime(section.end_at)}
                {section.expected_start_at && (
                  <span
                    className={styles.expected}
                    aria-label={`předpoklad ${formatTime(section.expected_start_at)}`}
                  >
                    {" "}≈ {formatTime(section.expected_start_at)}
                  </span>
                )}
              </span>
              <Button
                iconOnly
                size="sm"
                weight="ghost"
                // Keeps only the destructive hover tint.
                className={styles.sectionRemove}
                aria-label={`Odebrat část ${section.name}`}
                disabled={settled}
                startIcon={<X aria-hidden="true" size={16} />}
                onClick={() =>
                  confirm({
                    title: `Odebrat část „${section.name}“?`,
                    description: (
                      <>
                        Kategorie a bloky z této části se připojí k sousední v pořadí,
                        v jakém jsou teď. Zmizí jen rozdělení a název části.
                      </>
                    ),
                    confirmLabel: "Ano, odebrat",
                    cancelLabel: "Ne",
                    onConfirm: onDelete,
                  })
                }
              />
            </span>
          }
        />
      ) : undefined}
      <Card.Content padding="none">
        {/* A labelled region per part: it is what a screen reader lands
            on when a block announces which part it moved into. */}
        <div
          ref={setNodeRef}
          role="group"
          aria-label={`Část ${section.name}`}
          className={
            handlers.dropHint?.key === `s:${section.id}` && !handlers.dropHint.before
              ? styles.dropEnd
              : undefined
          }
        >
          <SortableContext
            items={section.items.map((i) => `i:${i.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {/* One separator opens the part, then each row carries its own
                trailing one INSIDE the sortable node — a separator left
                outside stays put while the row above it slides, which is
                what made the gaps look wrong mid-drag. */}
            {section.items.length > 0 && (
              <Separator
                onClick={() => handlers.onInsertAt(section.id)}
                disabled={handlers.locked}
                edge="first"
              />
            )}
            {section.items.map((item, i) => (
              <SortableItem
                key={item.id}
                item={item}
                sectionId={section.id}
                handlers={handlers}
                last={i === section.items.length - 1}
              />
            ))}
          </SortableContext>
          {section.items.length === 0 && (
            // An empty part has no boundary between rows to hang a
            // separator on, and one pinned to the top edge of the card was
            // both hard to hit and pointing at nothing. The whole empty
            // area is the control instead.
            <EmptyPart
              onClick={() => handlers.onInsertAt(section.id)}
              disabled={handlers.locked}
            />
          )}
        </div>

        {renaming && (
          <RenameSectionModal
            section={section}
            onClose={() => setRenaming(false)}
            onRename={onRename}
          />
        )}
      </Card.Content>
    </Card>
  );
}

/** An empty part: a drop zone that is also the "insert a block" button. */
function EmptyPart({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  if (disabled) {
    return (
      <div className={styles.emptySection}>
        <Text variant="muted">Zatím prázdné.</Text>
      </div>
    );
  }
  return (
    // eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- a full-bleed drop target between blocks, not a text button (§23)
    <button
      type="button"
      className={`${styles.emptySection} ${styles.emptyButton}`}
      onClick={onClick}
      aria-label="Vložit blok"
    >
      <Text variant="muted">Zatím prázdné. Přetáhněte sem kategorii nebo vložte blok.</Text>
      <span className={styles.emptyPlus}>
        <Plus aria-hidden="true" size={14} />
      </span>
    </button>
  );
}

/** The whole strip is the button, with a plus in the middle. */
function Separator({
  onClick,
  disabled,
  edge,
}: {
  onClick: () => void;
  disabled: boolean;
  // The two that sit on the card's own edges tuck their plus inward, or
  // `overflow: hidden` eats half of it. See the CSS.
  edge?: "first" | "last";
}) {
  if (disabled) return <div className={styles.separatorInert} />;
  const edgeClass =
    edge === "first" ? styles.separatorFirst : edge === "last" ? styles.separatorLast : "";
  return (
    // eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- a full-bleed drop target between blocks, not a text button (§23)
    <button
      type="button"
      className={edgeClass ? `${styles.separator} ${edgeClass}` : styles.separator}
      onClick={onClick}
      aria-label="Vložit blok"
    >
      <span className={styles.separatorPlus}>
        <Plus aria-hidden="true" size={14} />
      </span>
    </button>
  );
}

function SortableItem({
  item,
  sectionId,
  handlers,
  last,
  inLane = false,
}: {
  item: StartListItem;
  sectionId: ID;
  handlers: RowHandlers;
  // The trailing separator of the last item sits on the card's bottom edge.
  last: boolean;
  /** A block in a lane takes no separator: only discipline blocks go in a
   * lane, and a manual block is inserted into the part. */
  inLane?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `i:${item.id}`,
    // Locked: a frozen competition, or a published list not yet unlocked.
    disabled: handlers.locked,
  });
  const dimmed = handlers.confinedTo !== null && handlers.confinedTo !== item.id;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // The row travels in the DragOverlay, so what stays behind is the
    // gap it will occupy — not a ghost of itself.
    opacity: isDragging ? 0 : dimmed ? 0.35 : undefined,
  };

  const handle = handlers.locked ? null : (
    <Button
      iconOnly
      size="sm"
      weight="ghost"
      // Only the grab cursor and the inherited ink of a tinted row.
      className={styles.handle}
      aria-label={
        item.kind === "discipline"
          ? `Přesunout kategorii ${item.competition_category_name}`
          : item.kind === "lanes"
            ? "Přesunout dráhy"
            : "Přesunout blok"
      }
      startIcon={<GripVertical aria-hidden="true" size={16} />}
      {...attributes}
      {...listeners}
    />
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        [dimmed ? styles.dimmed : "", handlers.dropHint?.before === item.id ? styles.dropBefore : ""]
          .filter(Boolean)
          .join(" ") || undefined
      }
    >
      <ItemRows item={item} sectionId={sectionId} handlers={handlers} handle={handle} />
      {!inLane && (
        <Separator
          onClick={() => handlers.onInsertAt(sectionId, item.id)}
          disabled={handlers.locked}
          edge={last ? "last" : undefined}
        />
      )}
    </div>
  );
}

/** The body of one item, shared by the list and the drag overlay. */
function ItemRows({
  item,
  sectionId,
  handlers,
  handle,
}: {
  item: StartListItem;
  sectionId: ID;
  handlers: RowHandlers;
  handle: React.ReactNode;
}) {
  if (item.kind === "lanes") {
    return <LaneGroup item={item} sectionId={sectionId} handlers={handlers} handle={handle} />;
  }
  if (item.kind === "block") {
    const label = item.label || (item.block_kind ? BLOCK_KIND_LABELS[item.block_kind] : "Blok");
    return (
      <div className={item.parallel ? `${styles.block} ${styles.parallel}` : styles.block}>
        {handle}
        <span className={styles.blockTime}>
          {formatTime(item.start_at)}
          {item.parallel && <>–{formatTime(item.end_at)}</>}
          {item.expected_start_at && (
            <span
              className={styles.expected}
              aria-label={`předpoklad ${formatTime(item.expected_start_at)}`}
            >
              {" "}≈ {formatTime(item.expected_start_at)}
            </span>
          )}
        </span>
        <span className={styles.blockLabel}>
          {label}
          {!item.parallel && item.duration_seconds
            ? ` · ${formatDuration(item.duration_seconds)}`
            : ""}
        </span>
        {item.parallel && <Badge variant="info">souběžně</Badge>}
        <Button
          size="sm"
          weight="ghost"
          disabled={handlers.locked}
          onClick={() => handlers.onEditBlock(item, sectionId)}
        >
          Upravit
        </Button>
        <Button
          size="sm"
          weight="ghost"
          disabled={handlers.locked}
          onClick={() =>
            handlers.onDeleteBlock(item.id, label)
          }
        >
          Odebrat
        </Button>
      </div>
    );
  }

  const entries = item.entries ?? [];
  const picked = handlers.selected.includes(item.id);

  return (
    <>
      <div
        className={[
          styles.discipline,
          picked ? styles.picked : "",
          handlers.view === "compact" ? styles.disciplineCompact : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {handle}
        <span className={styles.disciplineName}>
          {/* A block whose category is gone used to render an empty
              heading, so the starts under it looked like they belonged to
              nobody. It says so now — and „Aktualizovat" is the way out:
              Build re-derives the categories from the entries, so a block
              left pointing at a deleted one is repaired rather than
              stranded. (An earlier version of this comment claimed the
              opposite and sent the organizer to delete the block by hand.) */}
          {item.competition_category_missing ? (
            <Badge variant="err">Kategorie už neexistuje</Badge>
          ) : (
            item.competition_category_name
          )}
          {/* How big the field is and how long it takes — the two numbers
              an organizer plans a day around, so they ride with the name
              in either view. The length is the block's own window, so it
              can never disagree with the times printed under it.
              („vystoupení" is the same in every number, so there is
              nothing to pluralize.) */}
          <span className={styles.disciplineMeta}>
            ({entries.length} vystoupení ~{" "}
            {formatDuration(item.end_at.diff(item.start_at, "second"))} min)
          </span>
        </span>
        <span className={styles.blockTime}>{formatTime(item.start_at)}</span>
        {handlers.selectable && !handlers.locked && (
          // A toggle button, not a Checkbox: selected means "click the
          // cross to drop it again", and the lib's Checkbox draws a tick
          // from its own stylesheet, which a page module has no business
          // rewriting. aria-pressed carries the same state a checkbox
          // would.
          // NOTE: the stated blocker is GONE as of 0.37.0 — `<Button pressed>`
          // grounds are now opaque mixes over the surface, so a toggle reads
          // the same inside a tinted container as on a card. What is untested
          // is the ink: this control takes the band's white through
          // `color: inherit`, and Button brings its own. Convert it and look,
          // rather than trusting either this comment or the old one.
          // eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- untested against 0.37.0's reworked pressed grounds; the control takes the tinted band's ink via `color: inherit` and Button brings its own
          <button
            type="button"
            className={picked ? `${styles.pick} ${styles.pickOn}` : styles.pick}
            aria-pressed={picked}
            aria-label={`Vybrat ${item.competition_category_name}`}
            onClick={() => handlers.onToggleSelect(item)}
          >
            {picked && <X aria-hidden="true" size={14} />}
          </button>
        )}
      </div>
      {handlers.view === "entries" && (
        <div className={handlers.movingBlock ? styles.dimmed : undefined}>
          <SortableContext
            items={entries.map((e) => `e:${e.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {entries.map((entry) => (
              <SortableEntry key={entry.id} entry={entry} handlers={handlers} />
            ))}
          </SortableContext>
        </div>
      )}
    </>
  );
}

/** A skupina drah (R19): one column per lane, each an ordered run of whole
 * discipline blocks, dragged in and out like any other block. */
function LaneGroup({
  item,
  sectionId,
  handlers,
  handle,
}: {
  item: StartListItem;
  sectionId: ID;
  handlers: RowHandlers;
  handle: React.ReactNode;
}) {
  const lanes = item.lanes ?? [];
  const mode = item.lane_mode ?? "parallel";
  return (
    <div className={styles.laneGroup}>
      <div className={styles.laneHeader}>
        {handle}
        <span className={styles.laneTitle}>
          Skupina drah · {LANE_MODE_LABELS[mode].toLowerCase()}
        </span>
        <span className={styles.blockTime}>
          {formatTime(item.start_at)}–{formatTime(item.end_at)}
        </span>
        {!handlers.locked && (
          <span className={styles.laneControls}>
            <Select
              aria-label="Režim drah"
              value={mode}
              onChange={(e) =>
                handlers.onLaneGroupChange(item.id, e.target.value as StartListLaneMode, lanes.length)
              }
            >
              {(Object.keys(LANE_MODE_LABELS) as StartListLaneMode[]).map((m) => (
                <option key={m} value={m}>
                  {LANE_MODE_LABELS[m]}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Počet drah"
              value={String(lanes.length)}
              onChange={(e) => handlers.onLaneGroupChange(item.id, mode, Number(e.target.value))}
            >
              {[2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n} {pluralizeCzech(n, ["dráha", "dráhy", "drah"])}
                </option>
              ))}
            </Select>
            <Button size="sm" weight="ghost" onClick={() => handlers.onDissolveLanes(item.id)}>
              Zrušit dráhy
            </Button>
          </span>
        )}
      </div>
      {handlers.overlay ? (
        // Just what is in it: no lane may be a drop target from here.
        <div className={styles.laneName}>
          {lanes
            .map((l) => `Dráha ${l.lane}: ${l.items.map((b) => b.competition_category_name).join(", ") || "prázdná"}`)
            .join(" · ")}
        </div>
      ) : (
      <div
        className={styles.lanes}
        style={{ "--lanes": lanes.length } as React.CSSProperties}
      >
        {lanes.map((lane) => (
          <LaneColumn
            key={lane.lane}
            groupId={item.id}
            lane={lane}
            sectionId={sectionId}
            handlers={handlers}
          />
        ))}
      </div>
      )}
    </div>
  );
}

function LaneColumn({
  groupId,
  lane,
  sectionId,
  handlers,
}: {
  groupId: ID;
  lane: StartListLane;
  sectionId: ID;
  handlers: RowHandlers;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `l:${groupId}:${lane.lane}` });
  return (
    <div
      ref={setNodeRef}
      role="group"
      aria-label={`Dráha ${lane.lane}`}
      className={[
        styles.lane,
        isOver ? styles.laneOver : "",
        handlers.dropHint?.key === `l:${groupId}:${lane.lane}` && !handlers.dropHint.before
          ? styles.dropEnd
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.laneName}>Dráha {lane.lane}</div>
      <SortableContext
        items={lane.items.map((i) => `i:${i.id}`)}
        strategy={verticalListSortingStrategy}
      >
        {lane.items.map((block, i) => (
          <SortableItem
            key={block.id}
            item={block}
            sectionId={sectionId}
            handlers={handlers}
            last={i === lane.items.length - 1}
            inLane
          />
        ))}
      </SortableContext>
      {lane.items.length === 0 && (
        <div className={styles.laneEmpty}>
          <Text variant="muted">
            {handlers.locked ? "Prázdná dráha." : "Přetáhněte sem kategorii."}
          </Text>
        </div>
      )}
    </div>
  );
}

function SortableEntry({
  entry,
  handlers,
}: {
  entry: NonNullable<StartListItem["entries"]>[number];
  handlers: RowHandlers;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `e:${entry.id}`,
    disabled: handlers.locked,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <EntryRow
        entry={entry}
        handlers={handlers}
        attributes={{ ...attributes }}
        listeners={listeners}
      />
    </div>
  );
}

function EntryRow({
  entry,
  handlers,
  attributes,
  listeners,
}: {
  entry: NonNullable<StartListItem["entries"]>[number];
  handlers: RowHandlers;
  attributes?: Record<string, unknown>;
  listeners?: Record<string, unknown>;
}) {
  const count = entry.competitors?.length ?? 0;
  // Sólo, duo and trio are named person by person, so the title holds the
  // names and the club is the second column. From mini-formace up the
  // team IS the competitor, and the server titles the row with it — so
  // printing the club again beside itself says nothing.
  const named = entry.title !== entry.team_name;

  return (
    <div className={entry.scratched ? `${styles.entry} ${styles.scratched}` : styles.entry}>
      {!handlers.locked && (
        <Button
          iconOnly
          size="sm"
          weight="ghost"
          className={styles.handle}
          aria-label={`Přesunout ${entry.title}`}
          startIcon={<GripVertical aria-hidden="true" size={16} />}
          {...attributes}
          {...listeners}
        />
      )}
      <span className={styles.num} aria-label={`Startovní číslo ${entry.display_number}`}>
        {entry.display_number}
      </span>
      <span className={styles.name}>
        {entry.title}
        {count > 1 && (
          <span className={styles.count} aria-label={`Počet soutěžících: ${count}`}>
            {" "}
            ({count})
          </span>
        )}
        {/* Beside the name, because that is what the organizer is
            reading when they work out who to ring. A scratched row is
            exempt: nobody plays it. */}
        {entry.music_missing && !entry.scratched && (
          <span
            className={styles.musicFlag}
            title="Chybí hudba"
            aria-label={`Chybí hudba: ${entry.title}`}
          >
            <VolumeX aria-hidden="true" size={16} />
          </span>
        )}
      </span>
      <span className={styles.club}>
        {named ? entry.team_name : ""}
        {/* Beside the club whether or not its name is repeated here: the
            state is what tells two same-named clubs apart. */}
        {entry.team_country && <TeamFlag code={entry.team_country} withCode={!named} />}
      </span>
      <span className={styles.cp} title="Číslo přihlášky">
        ČP {entry.entry_number}
      </span>
      <span className={styles.time}>
        {formatTime(entry.start_at)}
        {entry.expected_start_at && (
          <span
            className={styles.expected}
            title="Předpokládaný čas podle skluzu"
            aria-label={`předpoklad ${formatTime(entry.expected_start_at)}`}
          >
            {" "}≈ {formatTime(entry.expected_start_at)}
          </span>
        )}
      </span>
      {entry.missing_time_limit && (
        <Badge variant="warn" className={styles.flag}>
          chybí limit
        </Badge>
      )}
      {/* Reading the přihláška behind the row: who competes it, under
          which rules, and what will play. The organizer arranging a
          running order needs all three, and the row has space for none
          of them. */}
      <Button
        iconOnly
        size="sm"
        weight="ghost"
        className={styles.detailButton}
        aria-label={`Detail přihlášky ${entry.title}`}
        title="Detail přihlášky"
        onClick={() => handlers.onOpenDetail(entry.competition_entry_id)}
        startIcon={<Info aria-hidden="true" size={15} />}
      />
      {!handlers.frozen && (
      <Button
        iconOnly
        size="sm"
        weight="ghost"
        className={styles.scratchButton}
        aria-label={
          entry.scratched ? `Vrátit ${entry.title}` : `Přeškrtnout ${entry.title}`
        }
        onClick={() => handlers.onScratch(entry.id, !entry.scratched)}
        startIcon={
          entry.scratched ? (
            <RotateCcw aria-hidden="true" size={15} />
          ) : (
            <X aria-hidden="true" size={16} />
          )
        }
      />
      )}
    </div>
  );
}

/** What to render under the cursor for the row currently being dragged. */
function findDragged(days: StartListDay[], id: string): React.ReactNode {
  if (id.startsWith("i:")) {
    const itemId = id.slice(2);
    const item = findItem(days, itemId);
    const section = findSectionOfItem(days, itemId);
    if (!item || !section) return null;
    return (
      // The item as it is, entries included. Stripping them made the meta
      // line under the cursor read „(0 vystoupení ~ 12:30 min)" for a
      // block with twelve starts — and the strip was never needed anyway,
      // because OVERLAY_HANDLERS.view is "compact", which suppresses the
      // rows on its own.
      <ItemRows
        item={item}
        sectionId={section.id}
        handlers={OVERLAY_HANDLERS}
        handle={<GripVertical aria-hidden="true" size={16} />}
      />
    );
  }
  const entryId = id.slice(2);
  const item = findItemOfEntry(days, entryId);
  const entry = item?.entries?.find((e) => e.id === entryId);
  return entry ? <EntryRow entry={entry} handlers={OVERLAY_HANDLERS} /> : null;
}

/** The overlay is a picture of a row, not a working one. */
const OVERLAY_HANDLERS: RowHandlers = {
  view: "compact",
  onScratch: () => {},
  onOpenDetail: () => {},
  onEditBlock: () => {},
  onDeleteBlock: () => {},
  onInsertAt: () => {},
  onAddLanes: () => {},
  onLaneGroupChange: () => {},
  onDissolveLanes: () => {},
  onToggleSelect: () => {},
  onSelectMany: () => {},
  selected: [],
  selectable: false,
  confinedTo: null,
  movingBlock: false,
  locked: true,
  structureLocked: true,
  frozen: false,
  overlay: true,
};

function mapSections(
  days: StartListDay[],
  fn: (section: StartListSection) => StartListSection,
): StartListDay[] {
  return days.map((day) => ({ ...day, sections: day.sections.map(fn) }));
}

function mapItems(
  days: StartListDay[],
  fn: (item: StartListItem) => StartListItem,
): StartListDay[] {
  const deep = (item: StartListItem): StartListItem =>
    item.lanes
      ? fn({ ...item, lanes: item.lanes.map((l) => ({ ...l, items: l.items.map(deep) })) })
      : fn(item);
  return mapSections(days, (section) => ({ ...section, items: section.items.map(deep) }));
}

/** Every item of a part, the blocks in its skupiny drah included. */
function itemsDeep(section: StartListSection): StartListItem[] {
  return section.items.flatMap((i) => [i, ...(i.lanes ?? []).flatMap((l) => l.items)]);
}

function findItemOfEntry(days: StartListDay[], entryRowId: ID): StartListItem | undefined {
  for (const day of days) {
    for (const section of day.sections) {
      for (const item of itemsDeep(section)) {
        if ((item.entries ?? []).some((e) => e.id === entryRowId)) return item;
      }
    }
  }
  return undefined;
}

function findItem(days: StartListDay[], itemId: ID): StartListItem | undefined {
  for (const day of days) {
    for (const section of day.sections) {
      const found = itemsDeep(section).find((i) => i.id === itemId);
      if (found) return found;
    }
  }
  return undefined;
}

/** The part an item belongs to, a block in a lane included. */
function findSectionOfItem(days: StartListDay[], itemId: ID): StartListSection | undefined {
  for (const day of days) {
    for (const section of day.sections) {
      if (itemsDeep(section).some((i) => i.id === itemId)) return section;
    }
  }
  return undefined;
}

function findSection(days: StartListDay[], sectionId: ID): StartListSection | undefined {
  for (const day of days) {
    const found = day.sections.find((s) => s.id === sectionId);
    if (found) return found;
  }
  return undefined;
}

/** Where a dragged block will land: a container, before one of its items or
 * (no `before`) at its end. */
interface DropHint {
  key: string;
  before?: ID;
}

/** Where an item sits and moves: a part's own items, or one lane of a
 * skupina drah. Drop targets are keyed `s:<part>` and `l:<group>:<lane>`. */
type Container = { kind: "section"; sectionId: ID } | { kind: "lane"; groupId: ID; lane: number };

function containerKey(c: Container): string {
  return c.kind === "section" ? `s:${c.sectionId}` : `l:${c.groupId}:${c.lane}`;
}

function parseContainer(id: string): Container | undefined {
  if (id.startsWith("s:")) return { kind: "section", sectionId: id.slice(2) };
  if (id.startsWith("l:")) {
    const [groupId, lane] = id.slice(2).split(":");
    return { kind: "lane", groupId, lane: Number(lane) };
  }
  return undefined;
}

function containerOfItem(days: StartListDay[], itemId: ID): Container | undefined {
  for (const day of days) {
    for (const section of day.sections) {
      for (const item of section.items) {
        if (item.id === itemId) return { kind: "section", sectionId: section.id };
        for (const lane of item.lanes ?? []) {
          if (lane.items.some((i) => i.id === itemId)) {
            return { kind: "lane", groupId: item.id, lane: lane.lane };
          }
        }
      }
    }
  }
  return undefined;
}

function containerItems(days: StartListDay[], c: Container): StartListItem[] | undefined {
  if (c.kind === "section") return findSection(days, c.sectionId)?.items;
  return findItem(days, c.groupId)?.lanes?.find((l) => l.lane === c.lane)?.items;
}

function withContainerItems(
  days: StartListDay[],
  c: Container,
  items: StartListItem[],
): StartListDay[] {
  if (c.kind === "section") {
    return mapSections(days, (s) => (s.id === c.sectionId ? { ...s, items } : s));
  }
  return mapSections(days, (s) => ({
    ...s,
    items: s.items.map((i) =>
      i.id === c.groupId
        ? { ...i, lanes: (i.lanes ?? []).map((l) => (l.lane === c.lane ? { ...l, items } : l)) }
        : i,
    ),
  }));
}

/** „Zafixováno 1 startovní číslo" / „2 startovní čísla" / „5 startovních čísel". */
function fixedNumbers(n: number): string {
  return `${pluralizeCzech(n, ["Zafixováno", "Zafixována", "Zafixováno"])} ${n} ${pluralizeCzech(n, [
    "startovní číslo",
    "startovní čísla",
    "startovních čísel",
  ])}.`;
}

function sameOrder(a: ID[], b: ID[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** A missing list is the starting state, not a failure. */
function isNotFound(err: unknown): boolean {
  return !!err && typeof err === "object" && "status" in err && err.status === 404;
}

/** How late the running competition is today, whole minutes (R23). */
function delayMinutes(doc: StartListDocument): number {
  return Math.round(Math.max(0, ...doc.days.map((d) => d.delay_seconds ?? 0)) / 60);
}

